// PRD.318 — Dental Treatment Plan & Treatment Done.
//
// TWO RECORDS, ONE WORKFLOW
// -------------------------
//   dental_treatments        = the longitudinal PLAN (what should happen)
//   dental_treatment_records = what was actually PERFORMED, per encounter
//
// A plan's `status = 'done'` is workflow state, never the clinical record of
// what occurred (PRD §3). Marking a plan done creates a performed record; it
// does not move or copy the plan row.
//
// NO TREATMENT INFERENCE
// ----------------------
// Chart conditions and diagnoses supply TOOTH CONTEXT and a provenance label
// only. Nothing here maps caries→composite, pulpitis→RCT or missing→implant
// (PRD §16A.3). The clinician always chooses the treatment.
//
// Self-contained (no workspace-package imports) so it runs under node:test.

// ─── Lifecycle ───

export const DENTAL_TREATMENT_STATUSES = ['planned', 'in_progress', 'done', 'cancelled'] as const;

export type DentalTreatmentStatus = (typeof DENTAL_TREATMENT_STATUSES)[number];

export const DENTAL_TREATMENT_STATUS_LABELS: Record<DentalTreatmentStatus, string> = {
  planned: 'Planned',
  in_progress: 'In Progress',
  done: 'Done',
  cancelled: 'Cancelled',
};

/** Statuses the Treatment Plan view shows by default (PRD §9). */
export const ACTIVE_PLAN_STATUSES: readonly DentalTreatmentStatus[] = ['planned', 'in_progress'];

export function isDentalTreatmentStatus(value: unknown): value is DentalTreatmentStatus {
  return (
    typeof value === 'string' && (DENTAL_TREATMENT_STATUSES as readonly string[]).includes(value)
  );
}

/** Read a stored status, falling back to 'planned' for legacy/garbled values. */
export function readTreatmentStatus(value: string | null | undefined): DentalTreatmentStatus {
  return isDentalTreatmentStatus(value) ? value : 'planned';
}

// ─── Rows ───

export interface DentalTreatmentRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  created_encounter_id: string | null;
  treatment: string | null;
  /** Optional `appointment_services.id`. NULL for free-text treatment. */
  service_id: string | null;
  status: string | null;
  source: string | null;
  source_ref: string | null;
  source_diagnosis_id: string | null;
  source_label: string | null;
  created_by_user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface DentalTreatmentRecordRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  dental_treatment_id: string | null;
  treatment: string | null;
  /** Optional `appointment_services.id`. NULL for free-text treatment. */
  service_id: string | null;
  notes: string | null;
  performed_at: string | null;
  performed_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** Shared shape of both tooth-association tables. */
export interface ToothLinkRow {
  id: string;
  tooth: string | null;
  created_at?: string | null;
}

// ─── Tooth association identity ───

/**
 * Deterministic id for one (parent, tooth) pair.
 *
 * Same precedent as `diagnosisToothId`: two devices adding tooth 16 derive the
 * SAME id, so SyncWrite's `ON CONFLICT (id) DO UPDATE` converges them instead
 * of one violating the UNIQUE(parent, tooth) index and wedging the queue.
 */
export function dentalToothLinkId(parentId: string, tooth: string): string {
  return `${parentId}:${tooth}`;
}

function byFdi(a: string, b: string): number {
  return Number(a) - Number(b);
}

/** Canonical order, de-duplicated, blanks dropped. Never mutates the input. */
function normalizeTeeth(teeth: readonly string[]): string[] {
  return [...new Set(teeth.map((t) => String(t ?? '').trim()).filter(Boolean))].sort(byFdi);
}

/**
 * Group tooth links by their parent id.
 *
 * `parentIdOf` extracts the foreign key, so the same function serves both
 * `dental_treatment_teeth` and `dental_treatment_record_teeth` without either
 * table's column name leaking into this module.
 */
export function groupToothLinks<T extends ToothLinkRow>(
  rows: readonly T[],
  parentIdOf: (row: T) => string | null | undefined,
): Map<string, string[]> {
  const acc = new Map<string, string[]>();
  for (const row of rows) {
    const parent = parentIdOf(row);
    const tooth = (row.tooth ?? '').trim();
    if (!parent || !tooth) continue;
    acc.set(parent, [...(acc.get(parent) ?? []), tooth]);
  }
  const out = new Map<string, string[]>();
  for (const [parent, teeth] of acc) out.set(parent, normalizeTeeth(teeth));
  return out;
}

/**
 * Minimal change set between stored and selected teeth.
 *
 * A diff rather than delete-all-then-insert-all: with deterministic ids the
 * latter would queue a DELETE and a PUT for the same id on every save of an
 * unchanged tooth, churning sync and risking out-of-order arrival.
 */
export function diffTeeth(
  current: readonly string[],
  next: readonly string[],
): { added: string[]; removed: string[] } {
  const currentSet = new Set(normalizeTeeth(current));
  const nextSet = new Set(normalizeTeeth(next));
  return {
    added: [...nextSet].filter((t) => !currentSet.has(t)).sort(byFdi),
    removed: [...currentSet].filter((t) => !nextSet.has(t)).sort(byFdi),
  };
}

// ─── Selection ───

/**
 * The Treatment Plan view: active care only, oldest first.
 *
 * Done and cancelled items drop out of the plan but their rows are untouched —
 * status is a lifecycle field, never a reason to move or copy a row (PRD §9.2).
 */
export function selectActivePlan(rows: readonly DentalTreatmentRow[]): DentalTreatmentRow[] {
  return rows
    .filter((r) => ACTIVE_PLAN_STATUSES.includes(readTreatmentStatus(r.status)))
    .sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''));
}

/**
 * The Treatment Done view: performed records, newest first.
 *
 * These are the patient's actual treatment history across encounters — NOT the
 * plan rows whose status happens to be 'done' (PRD §12).
 */
export function selectPerformedRecords(
  rows: readonly DentalTreatmentRecordRow[],
): DentalTreatmentRecordRow[] {
  return [...rows].sort((a, b) => {
    const at = a.performed_at ?? a.created_at ?? '';
    const bt = b.performed_at ?? b.created_at ?? '';
    return bt.localeCompare(at);
  });
}

/** Performed records belonging to one plan, oldest first — its visit history. */
export function selectRecordsForTreatment(
  rows: readonly DentalTreatmentRecordRow[],
  treatmentId: string,
): DentalTreatmentRecordRow[] {
  return rows
    .filter((r) => r.dental_treatment_id === treatmentId)
    .sort((a, b) => {
      const at = a.performed_at ?? a.created_at ?? '';
      const bt = b.performed_at ?? b.created_at ?? '';
      return at.localeCompare(bt);
    });
}

// ─── Suggested treatment context ───

/** Where a suggestion came from. Presentation groups follow this. */
export type TreatmentSuggestionSource = 'chart' | 'diagnosis';

export interface TreatmentSuggestion {
  /** Stable identity of the source — see `sourceRef` notes on the schema. */
  ref: string;
  source: TreatmentSuggestionSource;
  /** Teeth to preselect. May be empty. */
  teeth: string[];
  /**
   * The condition or diagnosis wording, shown as SECONDARY context.
   * Never a proposed treatment.
   */
  label: string;
  /** Compact surface code from the chart, e.g. 'MO'. Empty for diagnosis. */
  surfaces: string;
  /** Set only for diagnosis-sourced suggestions. */
  diagnosisId?: string;
}

/**
 * Suppress suggestions already used to create a plan item (PRD §16A.5).
 *
 * Matching is on EXACT stable identity — the chart entry's composite ref, or
 * the diagnosis row's UUID. Label comparison is deliberately never used: a
 * caries suggestion that became "Composite Restoration" shares no wording with
 * its source, and fuzzy matching would either miss it or suppress the wrong
 * thing. When no stable identity exists the suggestion simply stays visible.
 */
export function filterUsedTreatmentSuggestions(
  suggestions: readonly TreatmentSuggestion[],
  treatments: readonly DentalTreatmentRow[],
): TreatmentSuggestion[] {
  const usedRefs = new Set<string>();
  for (const t of treatments) {
    // Cancelled plans free their suggestion again — the context is still
    // untreated, so re-offering it is correct.
    if (readTreatmentStatus(t.status) === 'cancelled') continue;
    if (t.source_ref) usedRefs.add(t.source_ref);
    if (t.source_diagnosis_id) usedRefs.add(t.source_diagnosis_id);
  }
  return suggestions.filter((s) => !usedRefs.has(s.ref));
}

/**
 * Group suggestions for the compact `Suggested` panel.
 *
 * Chart first, then diagnosis: the chart is the objective structured finding,
 * the diagnosis the interpretation. Empty groups are omitted so the panel never
 * renders a bare heading.
 */
export function groupTreatmentSuggestions(
  suggestions: readonly TreatmentSuggestion[],
): { source: TreatmentSuggestionSource; title: string; items: TreatmentSuggestion[] }[] {
  const order: { source: TreatmentSuggestionSource; title: string }[] = [
    { source: 'chart', title: 'From Chart' },
    { source: 'diagnosis', title: 'From Diagnosis' },
  ];
  return order
    .map((g) => ({ ...g, items: suggestions.filter((s) => s.source === g.source) }))
    .filter((g) => g.items.length > 0);
}

// ─── Service linkage ───

/** Minimal shape of an `appointment_services` row this module reads. */
export interface TreatmentServiceOption {
  id: string;
  name: string | null;
  active?: boolean | number | null;
}

/** The values a treatment is persisted with, whichever way it was entered. */
export interface TreatmentEntryValue {
  /** The clinical snapshot. Always populated. */
  treatment: string;
  /** Catalogue reference, or null for free text. */
  serviceId: string | null;
}

/**
 * Build a treatment entry from a chosen Service.
 *
 * SNAPSHOTS the service's CURRENT name into `treatment`. Historical records
 * must keep the wording documented at the time, so a later rename to
 * "Endodontic Root Canal Treatment" leaves an existing "Root Canal Treatment"
 * untouched — rendering never joins the live catalogue.
 *
 * A service with a blank name yields a blank label rather than a fabricated
 * one; the caller keeps whatever the clinician typed.
 */
export function treatmentFromService(service: TreatmentServiceOption): TreatmentEntryValue {
  return { treatment: (service.name ?? '').trim(), serviceId: service.id };
}

/**
 * Build a treatment entry from free text.
 *
 * `serviceId` is null and NO catalogue lookup happens: manual treatment is
 * never fuzzily matched to a Service, and never creates one.
 */
export function treatmentFromManualText(text: string): TreatmentEntryValue {
  return { treatment: text.trim(), serviceId: null };
}

/**
 * Filter the service catalogue for a typeahead.
 *
 * Returns EVERY active match by default. There used to be a cap of 8, which
 * silently hid most of a clinic's catalogue behind a search box that gave no
 * hint anything was missing; the dropdown scrolls, so it does not need one.
 * `limit` remains available for callers that genuinely want a short list.
 *
 * Inactive services are hidden from NEW selections — but this never affects an
 * already-linked treatment, whose label is a snapshot and renders regardless
 * (see `resolveTreatmentLabel`).
 */
export function searchTreatmentServices(
  services: readonly TreatmentServiceOption[],
  query: string,
  limit?: number,
): TreatmentServiceOption[] {
  const q = query.trim().toLowerCase();
  const matches = services.filter((s) => {
    if (s.active === false || s.active === 0) return false;
    if (!q) return true;
    return (s.name ?? '').toLowerCase().includes(q);
  });
  return limit === undefined ? matches : matches.slice(0, limit);
}

/**
 * What to display for a treatment.
 *
 * ALWAYS the stored snapshot — never the live service name. This is the whole
 * point of persisting `treatment` alongside `service_id`: a renamed, archived
 * or deleted Service leaves historical documentation intact.
 */
export function resolveTreatmentLabel(row: {
  treatment: string | null;
  service_id?: string | null;
}): string {
  return (row.treatment ?? '').trim();
}

// ─── Billing eligibility (clinical side only) ───

/**
 * A clinical record Billing may pull a Service from.
 *
 * This module EXPOSES eligibility; it never creates invoices, lines, charges or
 * any billing state. Billing decides what to add and resolves pricing with its
 * own rules at that moment.
 */
export interface BillableTreatment {
  /** Stable id of the originating record — the provenance key. */
  sourceId: string;
  /** Which clinical table it came from. */
  sourceKind: 'plan' | 'performed';
  serviceId: string;
  /** The snapshotted clinical label, for display in the picker. */
  label: string;
  teeth: string[];
}

/**
 * Service-linked treatments Billing could add.
 *
 * ELIGIBILITY IS ONLY `service_id != null`. Free-text treatment is perfectly
 * valid documentation but cannot become an invoice line, because matching
 * "Temporary dressing" against the catalogue would be exactly the fuzzy
 * matching this design forbids.
 *
 * Cancelled plans are excluded — they represent care that will not happen.
 * Status is otherwise NOT a filter: clinics bill before, during and after
 * treatment, so the clinical layer exposes what is linked and Billing chooses.
 */
export function selectBillableTreatments(params: {
  plans: readonly DentalTreatmentRow[];
  records: readonly DentalTreatmentRecordRow[];
  planTeeth: ReadonlyMap<string, string[]>;
  recordTeeth: ReadonlyMap<string, string[]>;
}): BillableTreatment[] {
  const out: BillableTreatment[] = [];

  for (const p of params.plans) {
    if (!p.service_id) continue;
    if (readTreatmentStatus(p.status) === 'cancelled') continue;
    out.push({
      sourceId: p.id,
      sourceKind: 'plan',
      serviceId: p.service_id,
      label: resolveTreatmentLabel(p),
      teeth: params.planTeeth.get(p.id) ?? [],
    });
  }

  for (const r of params.records) {
    if (!r.service_id) continue;
    out.push({
      sourceId: r.id,
      sourceKind: 'performed',
      serviceId: r.service_id,
      label: resolveTreatmentLabel(r),
      teeth: params.recordTeeth.get(r.id) ?? [],
    });
  }

  return out;
}

/**
 * Drop treatments already on an invoice.
 *
 * Keyed on the ORIGINATING RECORD's id (`source_id`), matching the
 * prescription→billing convention, never on service id, label or tooth: the
 * same Service can legitimately be performed twice, and two different
 * treatments may share one Service.
 *
 * Because a plan keeps its identity when its status changes, an item added
 * while Planned is still recognised once it is Done — a status change never
 * manufactures a new billable source.
 */
export function filterAlreadyBilled(
  candidates: readonly BillableTreatment[],
  billedSourceIds: ReadonlySet<string>,
): BillableTreatment[] {
  return candidates.filter((c) => !billedSourceIds.has(c.sourceId));
}

/**
 * Resolve what a treatment field commits, given what it was seeded with.
 *
 * Exists for Plan → Done. The draft is pre-filled with the plan's label and its
 * service reference, and accepting that unchanged MUST keep the reference —
 * treating it as fresh free text would silently null the `service_id` the
 * linkage exists to carry forward.
 *
 * Editing the text away from the seed drops the reference: the clinician is now
 * describing something other than the catalogue item, and a stale `service_id`
 * would let Billing add a Service that does not match the documented work.
 * Comparison is exact-after-trim against the seed — never a fuzzy or partial
 * match against the catalogue.
 */
export function resolveTreatmentEntry(params: {
  text: string;
  seededText: string;
  seededServiceId: string | null;
}): TreatmentEntryValue {
  const text = params.text.trim();
  if (params.seededServiceId && text === params.seededText.trim()) {
    return { treatment: text, serviceId: params.seededServiceId };
  }
  return treatmentFromManualText(text);
}
